import { useMemo, useCallback } from 'react';
import { Button } from '@mui/material';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import yup from '../../../../app/utils/customYup';

import EditModal, { IEditModalProps } from '../../../../components/EditModal';
import ChangeRequestForm from './ChangeRequestForm';
import Loader from '../../../../components/Loader';

import useLocale from '../../../../app/hooks/useLocale';

import { useUpdateDebitInformationMutation } from '../../queries';
import { ClientPublicService } from '../../../../app/api/ClientPublicService';
import { IDebitInfoUpdateParams } from '../../types';
import useAppForm from '../../../AppForm/hooks/useAppForm';
import { useDocumentsUploadMutation, IFormValuesDocumentItem } from '../../../../components/DocumentsUpload';

interface IEditPaymentDetailsModal extends Pick<IEditModalProps, 'onCancel'> {
  fileId: string;
  onOk?: () => void;
}

interface IEditPaymentDetailsValues extends ClientPublicService.IPADInfoChangeRequestCreatePublicDto {
  documents?: IFormValuesDocumentItem[];
}

const RequestChangeModal = ({ fileId, onCancel, onOk }: IEditPaymentDetailsModal) => {
  const { t } = useLocale();

  const { mutate: updateDebitInformation, isLoading: isUpdating } = useUpdateDebitInformationMutation() || {};
  const { mutateAsync: uploadDocuments } = useDocumentsUploadMutation() || {};

  const getRequiredByLabel = useCallback(
    (fieldName: string) => t.FIELD_IS_REQUIRED?.replace('{0}', fieldName),
    [t.FIELD_IS_REQUIRED]
  );

  const { appFormStatus } = useAppForm() || {};

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        accountName: yup.string().required(getRequiredByLabel(t.ACCOUNT_NAME)),
        bankName: yup.string().required(getRequiredByLabel(t.BANK_NAME)),
        bankAddress: yup.string().required(getRequiredByLabel(t.BANK_ADDRESS)),
        accountNumber: yup
          .string()
          .required(getRequiredByLabel(t.ACCOUNT_NUMBER))
          .matches(/^[\d#]{7,12}$/, t.BANKING_INFO_DETAILS_ERROR_INVALID_ACCOUNT_NUMBER),
        transitNumber: yup
          .string()
          .required(getRequiredByLabel(t.TRANSIT_NUMBER))
          .matches(/^\d{5}$/, t.BANKING_INFO_DETAILS_ERROR_INVALID_TRANSIT_NUMBER),
        branchNumber: yup
          .string()
          .required(getRequiredByLabel(t.BRANCH_NUMBER))
          .matches(/^\d{3}$/, t.BANKING_INFO_DETAILS_ERROR_INVALID_INSTITUTION),
        documents: yup
          .array()
          .of(
            yup.object({
              documentPrefixId: yup.string().required(t.THIS_IS_A_REQUIRED_FIELD),
            })
          )
          .required(t.SUPPORTING_DOCUMENTS_ARE_REQUIRED)
          .min(1),
      }),
    [
      getRequiredByLabel,
      t.ACCOUNT_NAME,
      t.ACCOUNT_NUMBER,
      t.BANK_ADDRESS,
      t.BANK_NAME,
      t.BRANCH_NUMBER,
      t.TRANSIT_NUMBER,
      t.THIS_IS_A_REQUIRED_FIELD,
      t.SUPPORTING_DOCUMENTS_ARE_REQUIRED,
      appFormStatus,
    ]
  );

  const handleUpdateDebitInformation = useCallback(
    async (values?: IEditPaymentDetailsValues) => {
      console.log('values', values);

      const newDocumentsIds = await uploadDocuments({
        fileId: fileId,
        files: values?.documents as IFormValuesDocumentItem[],
      });

      console.log('vnewIds', newDocumentsIds);

      updateDebitInformation(
        { fileId, ...values, documentIds: [...newDocumentsIds] as string[] } as IDebitInfoUpdateParams,
        {
          onSuccess: (data) => {
            if (data?.result === ClientPublicService.Result.Successful) {
              onOk?.();
            } else {
              toast.error(data?.messages?.[0]?.body || t.SOMETHING_WENT_WRONG);
            }
          },
          onError: () => {
            toast.error(t.SOMETHING_WENT_WRONG);
          },
        }
      );
    },
    [fileId, onOk, t.SOMETHING_WENT_WRONG, updateDebitInformation]
  );

  return (
    <EditModal title={t.CHANGE_PREAUTHORIZED_DEPOSIT} onCancel={onCancel}>
      <Loader loading={isUpdating} />
      <Formik
        initialValues={{
          accountName: '',
          bankName: '',
          bankAddress: '',
          accountNumber: '',
          transitNumber: '',
          branchNumber: '',
          documentIDs: [],
        }}
        onSubmit={(_: IEditPaymentDetailsValues, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
        validateOnMount
      >
        {({ values, isValid }) => {
          return (
            <>
              <ChangeRequestForm />

              <Button
                variant="contained"
                fullWidth
                onClick={async () => await handleUpdateDebitInformation(values)}
                sx={{ mt: 3 }}
                disabled={!isValid}
              >
                {t.REQUEST_CHANGE}
              </Button>
            </>
          );
        }}
      </Formik>
    </EditModal>
  );
};

export default RequestChangeModal;
